import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import AppWithAuth from './AppWithAuth';
import { KeycloakProvider } from './auth/SimpleKeycloakProvider';
import reportWebVitals from './reportWebVitals';

// CRA's error overlay treats every uncaught rejection as an app crash.
// MetaMask rejects on pages that are not a dapp. The reason is usually a
// plain object `{ message, stack }`, so String(reason) is "[object Object]"
// and a message-only filter misses it. public/index.html wraps
// addEventListener so webpack's overlay never sees these; this is the
// in-bundle backup plus a dismiss if an overlay iframe already mounted.
const noiseText = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) {
    return `${value.name} ${value.message} ${value.stack || ''}`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const parts = [record.message, record.stack, record.reason, record.fileName, record.source];
    try {
      parts.push(JSON.stringify(value));
    } catch {
      /* circular */
    }
    return parts.map(noiseText).join(' ');
  }
  return String(value);
};

const isExtensionNoise = (value: unknown): boolean => {
  const text = noiseText(value).toLowerCase();
  return (
    text.includes('chrome-extension://')
    || text.includes('moz-extension://')
    || text.includes('failed to connect to metamask')
    || text.includes('nkbihfbeogaeaoehlefnkodbefgpgknn')
    || (text.includes('metamask') && text.includes('inpage.js'))
  );
};

const dismissExtensionOverlay = () => {
  const nodes = [
    document.getElementById('webpack-dev-server-client-overlay'),
    document.getElementById('webpack-dev-server-overlay'),
    ...Array.from(document.querySelectorAll('iframe')).filter((frame) => {
      const id = frame.id || '';
      const title = frame.getAttribute('title') || '';
      return id.includes('webpack') || title.toLowerCase().includes('webpack');
    }),
  ].filter(Boolean) as HTMLElement[];

  nodes.forEach((node) => {
    let text = node.textContent || '';
    if (node instanceof HTMLIFrameElement) {
      try {
        text += ` ${node.contentDocument?.body?.innerText || ''}`;
      } catch {
        /* cross-origin */
      }
    }
    if (isExtensionNoise(text)) {
      node.remove();
    }
  });
};

window.addEventListener('error', (event) => {
  if (isExtensionNoise(event.filename) || isExtensionNoise(event.message) || isExtensionNoise(event.error)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    dismissExtensionOverlay();
  }
}, true);

window.addEventListener('unhandledrejection', (event) => {
  if (isExtensionNoise(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    dismissExtensionOverlay();
  }
}, true);

if (process.env.NODE_ENV === 'development') {
  new MutationObserver(dismissExtensionOverlay).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

// Check if auth is enabled
const AUTH_ENABLED = process.env.REACT_APP_KEYCLOAK_ENABLED === 'true';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    {AUTH_ENABLED ? (
      <KeycloakProvider>
        <AppWithAuth />
      </KeycloakProvider>
    ) : (
      <App />
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
